'use client';
import React from 'react';
import Link from 'next/link';
import { Check, Zap, Crown, Dna } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import Icon from '@/components/ui/AppIcon';


/** Public paid launch: Creator and Studio only (no Free plan card). */
const plans = [
  {
    id: 'plan-creator',
    name: 'Creator',
    price: '$12',
    period: '/month',
    icon: Zap,
    iconColor: 'text-primary',
    iconBg: 'bg-primary/15',
    descKey: 'pricingCreatorDesc',
    defaultDesc: '100 credits/month — unlock the workspace with Dream DNA™, brand kits, and creative tools.',
    features: [
      '100 credits/month',
      'Full Dream DNA™ Studio access',
      'All AI tools',
      'Advanced templates',
      'Priority generation queue',
    ],
    ctaKey: 'pricingStartCreator',
    ctaClass: 'btn-primary',
    highlight: true,
    badgeKey: 'pricingMostPopular',
  },
  {
    id: 'plan-studio',
    name: 'Studio',
    price: '$39',
    period: '/month',
    icon: Crown,
    iconColor: 'text-accent',
    iconBg: 'bg-accent/15',
    descKey: 'pricingStudioDesc',
    defaultDesc: '500 credits/month — for teams and agencies with commercial use and priority support.',
    features: [
      '500 credits/month',
      'Everything in Creator',
      'Team access (up to 5)',
      'Brand Kit generator',
      'Commercial use license',
      'Priority support',
    ],
    ctaKey: 'pricingStartStudio',
    ctaClass: 'btn-accent',
    highlight: false,
  },
];

export default function PricingPreview() {
  const { t } = useLanguage();
  const hp = t?.homePage;

  return (
    <section className="py-24 bg-card">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16">
        <div className="text-center mb-14">
          <p className="text-xs font-600 uppercase tracking-widest text-primary mb-3">{hp?.pricingLabel ?? 'Pricing'}</p>
          <h2 className="text-hero-md font-700 text-foreground mb-4">
            {hp?.pricingTitle ?? 'Simple, Transparent Pricing'}
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto text-base">
            {hp?.pricingSubtitle ??
              'Choose Creator or Studio to unlock your workspace. Cancel anytime. No hidden fees.'}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {plans?.map((plan) => {
            const Icon = plan?.icon;
            const badge = plan?.badgeKey ? (hp?.[plan.badgeKey as keyof typeof hp] ?? 'Most Popular') : null;
            const cta = hp?.[plan.ctaKey as keyof typeof hp] ?? plan.ctaKey;
            const period = plan.period ?? '/month';
            const desc = hp?.[plan.descKey as keyof typeof hp] ?? plan.defaultDesc;
            return (
              <div
                key={plan?.id}
                className={`relative rounded-2xl p-7 flex flex-col gap-5 transition-all duration-200
                  ${plan?.highlight
                    ? 'glass-card border-2 border-primary/40 glow-primary scale-[1.02]'
                    : 'glass-card border border-border'}
                `}
              >
                {badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-3 py-1 rounded-full text-xs font-700 bg-primary text-white">
                      {badge}
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl ${plan?.iconBg} flex items-center justify-center`}>
                    <Icon size={18} className={plan?.iconColor} />
                  </div>
                  <div>
                    <p className="font-700 text-foreground">{plan?.name}</p>
                    <p className="text-xs text-muted-foreground">{(desc as string)?.slice(0, 36)}...</p>
                  </div>
                </div>
                <div>
                  <span className="text-4xl font-800 text-foreground tabular-nums">{plan?.price}</span>
                  <span className="text-sm text-muted-foreground">{period}</span>
                </div>
                <ul className="flex flex-col gap-2.5">
                  {plan?.features?.map((feat) => (
                    <li key={`feat-${plan?.id}-${feat?.slice(0, 12)}`} className="flex items-center gap-2.5 text-sm text-muted-foreground">
                      <Check size={14} className="text-mint shrink-0" />
                      {feat}
                    </li>
                  ))}
                </ul>
                <Link
                  href="/sign-up-login-screen"
                  className={`${plan?.ctaClass} px-5 py-3 rounded-xl text-sm text-center mt-auto`}
                >
                  {cta}
                </Link>
              </div>
            );
          })}
        </div>

        <div className="text-center mt-10">
          <p className="text-xs text-muted-foreground flex items-center justify-center gap-1.5">
            <Dna size={12} className="text-primary" />
            {hp?.pricingDnaNote ?? 'Every plan includes Dream DNA Studio™ — our proprietary dream-to-reality methodology.'}
          </p>
        </div>
      </div>
    </section>
  );
}